"use client";

import Image from "next/image";
import { ImageIcon } from "lucide-react";
import ImageFallback from "@/app/components/ui/image-fallback";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { cn } from "@/lib/utils";

type OfferImageT = {
  url: string | null;
  alt_text?: string | null;
};

type OfferImageGalleryPropsT = {
  thumbnailImage?: OfferImageT | null;
  thumbnailAlt?: string | null;
  additionalImage1?: OfferImageT | null;
  additionalImage2?: OfferImageT | null;
  interieurImage?: OfferImageT | null;
  additionalImage3?: OfferImageT | null;
  additionalImage4?: OfferImageT | null;
  onImageClick: (imageUrl: string) => void;
  showGalleryButton: boolean;
  onGalleryButtonClick: () => void;
};

/**
 * OfferImageGallery
 *
 * Layout matches OfferPageSkeleton:
 * - Wrapper: Flex col on mobile, Grid 1.5fr/1fr on LG
 * - Left: Main Image
 * - Right: Grid (3 cols mobile, 2 cols LG with auto-rows 200px)
 */
export default function OfferImageGallery({
  thumbnailImage,
  thumbnailAlt,
  additionalImage1,
  additionalImage2,
  interieurImage,
  additionalImage3,
  additionalImage4,
  onImageClick,
  showGalleryButton,
  onGalleryButtonClick,
}: OfferImageGalleryPropsT) {
  const { isSkoda } = useBrand();
  const skodaImageTileClass =
    "rounded-none shadow-[5px_35px_35px_hsl(0_0%_40%_/_0.15)]";
  const hasInteriorImage = Boolean(interieurImage?.url);
  const mobileThirdImage = hasInteriorImage ? interieurImage : additionalImage3;
  const mobileThirdAlt = hasInteriorImage
    ? (interieurImage?.alt_text ?? "Innenraum")
    : (additionalImage3?.alt_text ?? "Fahrzeugbild");

  return (
    <div className="flex flex-col gap-4 pt-4 lg:grid lg:grid-cols-[1.5fr_1fr] lg:gap-3">
      {/* Thumbnail Image */}
      <div id="main-image" className="relative h-full w-full lg:col-span-1">
        {thumbnailImage?.url ? (
          <button
            onClick={() => onImageClick(thumbnailImage.url!)}
            className={cn(
              "relative h-full w-full cursor-pointer overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
            aria-label="Bildergalerie öffnen"
          >
            <Image
              src={thumbnailImage.url}
              alt={thumbnailAlt || thumbnailImage.alt_text || "Fahrzeugbild"}
              width={1000}
              height={1000}
              className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
            />
          </button>
        ) : (
          <div
            className={cn(
              "h-full w-full overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
          >
            <ImageFallback className="aspect-[16/9] h-full w-full" />
          </div>
        )}
        {/* Gallery Button Overlay - Mobile only, bottom left */}
        {showGalleryButton && (
          <button
            onClick={onGalleryButtonClick}
            className={cn(
              "absolute right-3 bottom-3 flex cursor-pointer items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-sm font-medium text-gray-800 shadow-lg transition-all hover:bg-gray-100 hover:shadow-xl lg:hidden",
              isSkoda && "rounded-none"
            )}
            aria-label="Bildergalerie öffnen"
          >
            <ImageIcon className="h-4 w-4" />
            Bildergalerie
          </button>
        )}
      </div>

      {/* Gallery Images */}
      <div
        id="gallery"
        className="relative grid grid-cols-3 gap-2 lg:col-span-1 lg:auto-rows-[200px] lg:grid-cols-2 lg:gap-3"
      >
        {/* Image 1 */}
        {additionalImage1?.url ? (
          <button
            onClick={() => onImageClick(additionalImage1.url!)}
            className={cn(
              "relative h-full w-full cursor-pointer overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
            aria-label="Bildergalerie öffnen"
          >
            <Image
              src={additionalImage1.url}
              alt={additionalImage1.alt_text || "Fahrzeugbild"}
              width={1000}
              height={1000}
              sizes="(max-width: 768px) 33vw, (max-width: 1200px) 50vw, 33vw"
              className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
            />
          </button>
        ) : (
          <div
            className={cn(
              "h-full w-full overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
          >
            <ImageFallback className="aspect-[16/9] h-full w-full" size="sm" />
          </div>
        )}

        {/* Image 2 */}
        {additionalImage2?.url ? (
          <button
            onClick={() => onImageClick(additionalImage2.url!)}
            className={cn(
              "relative h-full w-full cursor-pointer overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
            aria-label="Bildergalerie öffnen"
          >
            <Image
              src={additionalImage2.url}
              alt={additionalImage2.alt_text || "Fahrzeugbild"}
              width={113}
              height={70}
              sizes="(max-width: 768px) 33vw, (max-width: 1200px) 50vw, 33vw"
              className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
            />
          </button>
        ) : (
          <div
            className={cn(
              "h-full w-full overflow-hidden rounded-lg",
              isSkoda && skodaImageTileClass
            )}
          >
            <ImageFallback className="aspect-[16/9] h-full w-full" size="sm" />
          </div>
        )}

        {/* Interior image (if present) OR third additional image (mobile fallback) */}
        {mobileThirdImage?.url ? (
          <button
            onClick={() => onImageClick(mobileThirdImage.url!)}
            className={cn(
              "relative h-full w-full cursor-pointer overflow-hidden rounded-lg",
              hasInteriorImage ? "lg:col-span-2 lg:row-span-1" : "lg:hidden",
              isSkoda && skodaImageTileClass
            )}
            aria-label="Bildergalerie öffnen"
          >
            <Image
              src={mobileThirdImage.url}
              alt={mobileThirdAlt}
              width={1000}
              height={1000}
              sizes="(max-width: 768px) 33vw, (max-width: 1200px) 100vw, 33vw"
              className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
            />
          </button>
        ) : (
          <div
            className={cn(
              "h-full w-full overflow-hidden rounded-lg",
              hasInteriorImage ? "lg:col-span-2 lg:row-span-1" : "lg:hidden",
              isSkoda && skodaImageTileClass
            )}
          >
            <ImageFallback className="aspect-[16/9] h-full w-full" size="sm" />
          </div>
        )}

        {/* Desktop fallback layout without interior image: use additional images 3 + 4 */}
        {!hasInteriorImage &&
          (additionalImage3?.url ? (
            <button
              onClick={() => onImageClick(additionalImage3.url!)}
              className={cn(
                "relative hidden h-full w-full cursor-pointer overflow-hidden rounded-lg lg:block",
                isSkoda && skodaImageTileClass
              )}
              aria-label="Bildergalerie öffnen"
            >
              <Image
                src={additionalImage3.url}
                alt={additionalImage3.alt_text || "Fahrzeugbild"}
                width={1000}
                height={1000}
                sizes="(max-width: 1200px) 50vw, 33vw"
                className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
              />
            </button>
          ) : (
            <div
              className={cn(
                "hidden h-full w-full overflow-hidden rounded-lg lg:block",
                isSkoda && skodaImageTileClass
              )}
            >
              <ImageFallback
                className="aspect-[16/9] h-full w-full"
                size="sm"
              />
            </div>
          ))}

        {!hasInteriorImage &&
          (additionalImage4?.url ? (
            <button
              onClick={() => onImageClick(additionalImage4.url!)}
              className={cn(
                "relative hidden h-full w-full cursor-pointer overflow-hidden rounded-lg lg:block",
                isSkoda && skodaImageTileClass
              )}
              aria-label="Bildergalerie öffnen"
            >
              <Image
                src={additionalImage4.url}
                alt={additionalImage4.alt_text || "Fahrzeugbild"}
                width={1000}
                height={1000}
                sizes="(max-width: 1200px) 50vw, 33vw"
                className="aspect-[16/9] h-full w-full object-cover transition-opacity hover:opacity-90"
              />
            </button>
          ) : (
            <div
              className={cn(
                "hidden h-full w-full overflow-hidden rounded-lg lg:block",
                isSkoda && skodaImageTileClass
              )}
            >
              <ImageFallback
                className="aspect-[16/9] h-full w-full"
                size="sm"
              />
            </div>
          ))}

        {/* Gallery Button Overlay - Desktop only, bottom right of the gallery container (or specific image) */}
        {/* Note: In the skeleton, this overlay isn't explicitly shown but we need to place it somewhere reasonable for desktop. 
            In previous design it was absolute to the gallery container. 
            Since gallery is a grid now, relative positioning on the container works. */}
        {showGalleryButton && (
          <div className="pointer-events-none absolute right-3 bottom-3 hidden lg:block">
            <button
              onClick={onGalleryButtonClick}
              className={cn(
                "pointer-events-auto flex cursor-pointer items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-sm font-medium text-gray-800 shadow-lg transition-all hover:bg-gray-100 hover:shadow-xl",
                isSkoda && "rounded-none"
              )}
              aria-label="Bildergalerie öffnen"
            >
              <ImageIcon className="h-4 w-4" />
              Bildergalerie
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
